import React, { useEffect, useState } from 'react';
import type { UploadedWorksheet } from '../../classroom/missionLayers';

const AGE_LABELS: Record<UploadedWorksheet['ageGroup'], string> = {
  'k-2': 'K–2',
  '3-5': '3–5',
  '6-8': '6–8',
  'k-8': 'K–8',
};

const isImage = (worksheet: UploadedWorksheet) => /\.(avif|gif|jpe?g|png|webp)(?:$|\?)/i.test(worksheet.title) || /^data:image\//i.test(worksheet.url);

async function downloadWorksheet(worksheet: UploadedWorksheet) {
  try {
    const response = await fetch(worksheet.url);
    if (!response.ok) throw new Error(`Download failed (${response.status})`);
    const objectUrl = URL.createObjectURL(await response.blob());
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = worksheet.title;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    window.open(worksheet.url, '_blank', 'noopener,noreferrer');
  }
}

const WorksheetDownloads: React.FC<{ worksheets: UploadedWorksheet[]; onPrint?: () => void }> = ({ worksheets, onPrint }) => {
  const [selected, setSelected] = useState<UploadedWorksheet | null>(null);

  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && setSelected(null);
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selected]);

  if (!worksheets.length && !onPrint) return null;

  return (
    <>
      <div className="cd-worksheet-list">
        {onPrint && <div className="cd-worksheet-row"><span className="cd-worksheet-age">PDF</span><strong>Print student work · Universe cards</strong><button type="button" className="cd-btn cd-btn-compact" onClick={onPrint}>View readiness</button><button type="button" className="cd-btn cd-btn-compact" onClick={onPrint}>Download</button></div>}
        {worksheets.map((worksheet) => (
          <div className="cd-worksheet-row" key={`${worksheet.ageGroup}-${worksheet.id}`}>
            <span className="cd-worksheet-age">{AGE_LABELS[worksheet.ageGroup]}</span>
            <strong title={worksheet.title}>{worksheet.title}</strong>
            <button type="button" className="cd-btn cd-btn-compact" onClick={() => setSelected(worksheet)}>View</button>
            <button type="button" className="cd-btn cd-btn-compact" onClick={() => void downloadWorksheet(worksheet)}>Download</button>
          </div>
        ))}
      </div>
      {selected ? (
        <div className="cd-worksheet-viewer" role="dialog" aria-modal="true" aria-label={`View ${selected.title}`}>
          <header>
            <div><span>{AGE_LABELS[selected.ageGroup]} worksheet</span><strong>{selected.title}</strong></div>
            <button type="button" className="cd-btn" onClick={() => void downloadWorksheet(selected)}>Download</button>
            <button type="button" className="cd-modal-close" onClick={() => setSelected(null)} aria-label="Close worksheet">×</button>
          </header>
          <main>
            {isImage(selected)
              ? <img src={selected.url} alt={selected.title} />
              : <iframe src={`${selected.url}#view=FitH`} title={selected.title} />}
          </main>
        </div>
      ) : null}
    </>
  );
};

export default WorksheetDownloads;
